import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Container, Typography } from '@mui/material';
import { useCallback, useContext, useState } from 'react';
import ClientOnboardingApi from '../../../../../apis/ClientOnboardingApi';
import SnackbarContext from '../../../../../contexts/SnackbarContext';
import useApi from '../../../../../lib/useApi';
import CommonStepProps from '../../../lib/CommonStepProps';
import WelcomeSection from './components/WelcomeSection';

/**
 * The welcome step for the client onboarding
 */
const WelcomeStep = ({
  isStepComplete,
  setStepComplete,
  moveToNextStep,
  config,
}: CommonStepProps) => {
  const [isCompleting, setIsCompleting] = useState(false);
  const clientOnboardingApi = useApi(ClientOnboardingApi);
  const snackbarContext = useContext(SnackbarContext);

  const completeStep = useCallback(() => {
    // If the step is complete, end early
    if (isStepComplete) {
      moveToNextStep();
      return;
    }

    // Complete the step
    setIsCompleting(true);
    clientOnboardingApi
      .CompleteWelcomeStep()
      .then(() => {
        setStepComplete();
        moveToNextStep();
      })
      .catch(() => {
        snackbarContext.setAlert(
          <Alert variant="filled" severity="error">
            An error occurred while completing the welcome step. Please try
            again.
          </Alert>
        );
        snackbarContext.triggerSnackbar();
      })
      .finally(() => {
        setIsCompleting(false);
      });
  }, [
    clientOnboardingApi,
    isStepComplete,
    moveToNextStep,
    setStepComplete,
    snackbarContext,
  ]);

  const checklistItems = [
    'Business legal information, such as name, address, and phone.',
    'How many licenses your organization needs.',
    'Your payment method. You may pay by credit card or ACH transfer.',
    'The date you would like training to be reset each year.',
    'The emails for the people who will be managing your organization for the training.',
  ];

  return (
    <Box position="relative" height="100%">
      <Container
        maxWidth="md"
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <WelcomeSection title="Directions">
          <Typography sx={{ width: '100%' }}>
            Welcome to the S3G Security Client Onboarding! You will be taken
            through a series of steps to complete it. Your progress will be
            saved as you complete each step. Once all the steps are complete,
            your organization can begin the S3G Security training.
          </Typography>
        </WelcomeSection>
        <WelcomeSection title="Checklist">
          <Typography marginBottom="1em">
            Here is a checklist of items you will need to complete the
            onboarding. More information on the items will be provided as you
            work through the steps.
          </Typography>
          <Box display="flex" flexDirection="column" gap=".5em">
            {checklistItems.map((c, i) => (
              <Box key={i} display="flex" gap=".75em">
                <CheckCircleOutlineRoundedIcon color="success" />
                <Typography>{c}</Typography>
              </Box>
            ))}
          </Box>
        </WelcomeSection>
        <WelcomeSection title="Contact">
          <Typography gutterBottom>
            If you need help with anything or have any questions, please do not
            hesitate to contact us.
          </Typography>
          <Typography marginBottom="1em">{config.supportEmail}</Typography>
        </WelcomeSection>
        <LoadingButton
          loading={isCompleting}
          onClick={completeStep}
          size="large"
          variant="contained"
          sx={{
            marginY: '2em',
          }}
        >
          Begin client onboarding
        </LoadingButton>
      </Container>
    </Box>
  );
};

export default WelcomeStep;
